import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, Text, Image, TouchableOpacity } from 'react-native';
import { Title, Paragraph, Button, Card, Chip } from 'react-native-paper';
import { useTheme as usePaperTheme } from 'react-native-paper';
import apiService from '../services/ApiService';
import { getLatestBlogPost, formatBlogDate } from '../utils/blogUtils';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedContainer, ThemedText } from '../components/ThemedContainer';
import { DebugPanel } from '../components/DebugPanel';

export default function HomeScreen({ navigation }: { navigation: any }) {
  const paperTheme = usePaperTheme();
  const { themeData } = useTheme();
  
  const [stats, setStats] = useState([
    { icon: '🍺', value: 'Loading...', label: 'Venues' },
    { icon: '🍹', value: 'Loading...', label: 'Cocktails' },
    { icon: '🥃', value: 'Loading...', label: 'Rums' },
    { icon: '🏭', value: 'Loading...', label: 'Distilleries' },
  ]);
  const [latestBlogPost, setLatestBlogPost] = useState<any>(null);
  const [featuredVenues, setFeaturedVenues] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setIsLoading(true);
    setHasError(false);
    setErrorMessage('');
    
    try {
      await Promise.all([
        loadDatabaseStats(),
        loadLatestBlogPost(),
        loadFeaturedVenues()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      setHasError(true);
      setErrorMessage('Unable to connect to RumDB server. Please check if the backend is running.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadDatabaseStats = async () => {
    try {
      console.log('🔍 Loading database stats...');
      const databaseStats = await apiService.getDatabaseStats();
      console.log('✅ Database stats received:', databaseStats);
      
      if (!databaseStats) {
        console.error('❌ Database stats is undefined or null');
        return;
      }
      
      setStats(prevStats => 
        prevStats.map(stat => {
          switch (stat.label) {
            case 'Venues':
              return { ...stat, value: (databaseStats.venues || 0).toString() };
            case 'Rums':
              return { ...stat, value: (databaseStats.rums || 0).toString() };
            case 'Cocktails':
              return { ...stat, value: (databaseStats.cocktails || 0).toString() };
            case 'Distilleries':
              return { ...stat, value: (databaseStats.distilleries || 0).toString() };
            default:
              return stat;
          }
        })
      );
    } catch (error) {
      console.error('❌ Error loading database stats:', error);
    }
  };

  const loadLatestBlogPost = () => {
    const latestPost = getLatestBlogPost();
    setLatestBlogPost(latestPost);
  };

  const handleImageError = (imageUrl: string) => {
    setImageErrors(prev => new Set(prev).add(imageUrl));
  };

  const renderVenueImage = (venue: any) => {
    const hasImageError = imageErrors.has(venue.hero_image);
    
    if (!venue.hero_image || hasImageError) {
      // Fallback to a placeholder with venue name
      return (
        <View style={[styles.venueImage, styles.placeholderImage, { backgroundColor: themeData.colors.surface }]}>
          <Text style={[styles.placeholderText, { color: themeData.colors.textSecondary }]}>
            🍹
          </Text>
          <Text style={[styles.placeholderText, { color: themeData.colors.textSecondary, fontSize: 12 }]}>
            {venue.name}
          </Text>
        </View>
      );
    }

    return (
      <Image
        source={{ uri: venue.hero_image }}
        style={styles.venueImage}
        onError={() => handleImageError(venue.hero_image)}
        resizeMode="cover"
      />
    );
  };

  const loadFeaturedVenues = async () => {
    try {
      console.log('🔍 Loading featured venues...');
      const venues = await apiService.getVenues();
      console.log('✅ Venues received:', venues);
      
      if (!venues || !Array.isArray(venues)) {
        console.error('❌ Venues is not an array:', venues);
        throw new Error('Invalid venues data');
      }
      
      // Force the specific venues we want, regardless of API data
      const desiredVenues = [
        "Trader Sam's Grog Grotto",
        "Ka Pakele", 
        "The Honu",
        "Morgan's Cove"
      ];
      
      // Try to find the desired venues in the API data
      const selectedVenues = desiredVenues.map(desiredName => {
        const found = venues.find((venue: any) => 
          venue.name.toLowerCase().includes(desiredName.toLowerCase()) ||
          desiredName.toLowerCase().includes(venue.name.toLowerCase())
        );
        return found;
      }).filter(Boolean);
      
      console.log('🔍 Found desired venues from API:', selectedVenues.map((v: any) => `${v.name} (${v.city})`));
      
      // If we found all desired venues, use them
      if (selectedVenues.length === 4) {
        setFeaturedVenues(selectedVenues);
        return;
      }
      
      // Otherwise, fall back to hardcoded data
      console.log('Not all desired venues found in API, using fallback data');
      throw new Error('Not all desired venues found');
    } catch (error) {
      console.error('Error loading featured venues:', error);
      // Fallback to hardcoded data with the 4 specific bars requested
      setFeaturedVenues([
        {
          name: "Trader Sam's Grog Grotto",
          city: "Lake Buena Vista",
          region: "FL",
          description: "Disney's tiki bar with themed cocktails and immersive atmosphere",
          venue_type: "tiki_bar",
          hero_image: "http://localhost:3001/images/bars/trader-sams.jpg"
        },
        {
          name: "Ka Pakele",
          city: "Dunedin",
          region: "FL",
          description: "Reservation-only rum bar with exclusive rums",
          venue_type: "tiki_bar",
          hero_image: "http://localhost:3001/images/bars/ka-pakele.jpg"
        },
        {
          name: "The Honu",
          city: "Dunedin",
          region: "FL",
          description: "A tropical paradise nestled in the heart of Dunedin, The Honu transports you to the enchanting islands of Hawaii and Polynesia. This magical tiki bar, hidden behind a Hawaiian/Polynesian restaurant, offers an immersive escape where palm trees sway, tiki torches flicker, and the spirit of aloha flows as freely as the expertly crafted cocktails. Step into a world where every detail whispers of distant shores and ancient traditions.",
          venue_type: "tiki_bar",
          hero_image: "http://localhost:3001/images/bars/the-honu.jpg"
        },
        {
          name: "Morgan's Cove",
          city: "Tampa",
          region: "FL",
          description: "Nautical-themed speakeasy with tiki cock and pirate atmosphere",
          venue_type: "tiki_bar",
          hero_image: "http://localhost:3001/images/bars/morgans-cove.jpg"
        }
      ]);
    }
  };

  const renderStats = () => (
    <ThemedContainer variant="card" style={styles.statsContainer}>
      <ThemedText size="title" style={styles.sectionTitle}>
        Database Overview
      </ThemedText>
      <View style={styles.statsGrid}>
        {stats.map((stat, index) => (
          <View key={index} style={styles.statItem}>
            <Text style={[styles.statIcon, { color: themeData.colors.primary }]}>
              {stat.icon}
            </Text>
            <ThemedText size="large" style={styles.statValue}>
              {stat.value}
            </ThemedText>
            <ThemedText variant="secondary" size="small">
              {stat.label}
            </ThemedText>
          </View>
        ))}
      </View>
    </ThemedContainer>
  );

  const renderFeaturedVenues = () => (
    <ThemedContainer variant="card" style={styles.venuesContainer}>
      <ThemedText size="title" style={styles.sectionTitle}>
        Featured Tiki Bars
      </ThemedText>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.venuesScroll}>
        {featuredVenues.map((venue, index) => (
          <Card key={index} style={[styles.venueCard, { backgroundColor: themeData.colors.surface }]}>
            {renderVenueImage(venue)}
            <Card.Content style={styles.venueContent}>
              <ThemedText size="large" style={styles.venueName}>
                {venue.name}
              </ThemedText>
              <ThemedText variant="secondary" size="small">
                {venue.city}, {venue.region}
              </ThemedText>
              <ThemedText variant="secondary" size="small" style={styles.venueDescription}>
                {venue.description}
              </ThemedText>
            </Card.Content>
          </Card>
        ))}
      </ScrollView>
    </ThemedContainer>
  );

  const renderLatestBlogPost = () => {
    if (!latestBlogPost) return null;

    return (
      <ThemedContainer variant="card" style={styles.blogContainer}>
        <ThemedText size="title" style={styles.sectionTitle}>
          Latest from the Blog
        </ThemedText>
        <Card style={[styles.blogCard, { backgroundColor: themeData.colors.surface }]}>
          <Card.Content>
            <ThemedText size="large" style={styles.blogTitle}>
              {latestBlogPost.title}
            </ThemedText>
            <ThemedText variant="secondary" size="small" style={styles.blogDate}>
              {formatBlogDate(latestBlogPost.date)}
            </ThemedText>
            <ThemedText variant="secondary" size="medium" style={styles.blogExcerpt}>
              {latestBlogPost.excerpt}
            </ThemedText>
            <Button
              mode="contained"
              onPress={() => navigation.navigate('Blog')}
              style={[styles.blogButton, { backgroundColor: themeData.colors.primary }]}
              labelStyle={{ color: '#FFFFFF' }}
            >
              Read More
            </Button>
          </Card.Content>
        </Card>
      </ThemedContainer>
    );
  };

  const renderErrorState = () => (
    <ThemedContainer variant="card" style={styles.errorContainer}>
      <View style={styles.errorContent}>
        <Text style={[styles.errorIcon, { color: themeData.colors.error }]}>
          🚨
        </Text>
        <ThemedText size="title" style={styles.errorTitle}>
          Server Offline
        </ThemedText>
        <ThemedText variant="secondary" size="medium" style={styles.errorMessage}>
          {errorMessage}
        </ThemedText>
        <ThemedText variant="secondary" size="small" style={styles.errorHint}>
          Tell Chris to get his server online! 😄
        </ThemedText>
        <Button
          mode="contained"
          onPress={loadAllData}
          style={[styles.retryButton, { backgroundColor: themeData.colors.primary }]}
          labelStyle={{ color: '#FFFFFF' }}
        >
          Try Again
        </Button>
      </View>
    </ThemedContainer>
  );

  const renderLoadingState = () => (
    <ThemedContainer variant="card" style={styles.loadingContainer}>
      <View style={styles.loadingContent}>
        <Text style={[styles.loadingIcon, { color: themeData.colors.primary }]}>
          🍹
        </Text>
        <ThemedText size="title" style={styles.loadingTitle}>
          Loading RumDB...
        </ThemedText>
        <ThemedText variant="secondary" size="medium" style={styles.loadingMessage}>
          Mixing up some delicious data for you
        </ThemedText>
      </View>
    </ThemedContainer>
  );

  if (isLoading) {
    return (
      <View style={[styles.container, { backgroundColor: themeData.colors.background }]}>
        <ThemedContainer variant="background" style={styles.content}>
          {renderLoadingState()}
        </ThemedContainer>
      </View>
    );
  }

  if (hasError) {
    return (
      <View style={[styles.container, { backgroundColor: themeData.colors.background }]}>
        <ThemedContainer variant="background" style={styles.content}>
          {renderErrorState()}
        </ThemedContainer>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: themeData.colors.background }]}>
      <ScrollView>
        <ThemedContainer variant="background" style={styles.content}>
                  {renderStats()}
        {renderFeaturedVenues()}
        {renderLatestBlogPost()}
      </ThemedContainer>
      </ScrollView>
      
      {/* Debug Button - Only show in development */}
      {__DEV__ && (
        <TouchableOpacity
          style={styles.debugButton}
          onPress={() => setShowDebugPanel(true)}
        >
          <Text style={styles.debugButtonText}>🔧</Text>
        </TouchableOpacity>
      )}
      
      {/* Debug Panel */}
      <DebugPanel
        isVisible={showDebugPanel}
        onClose={() => setShowDebugPanel(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
  },
  statsContainer: {
    marginBottom: 24,
  },
  sectionTitle: {
    marginBottom: 16,
    textAlign: 'center',
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  statItem: {
    width: '48%',
    alignItems: 'center',
    padding: 16,
    marginBottom: 12,
  },
  statIcon: {
    fontSize: 32,
    marginBottom: 8,
  },
  statValue: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  venuesContainer: {
    marginBottom: 24,
  },
  venuesScroll: {
    marginHorizontal: -8,
  },
  venueCard: {
    width: 280,
    marginHorizontal: 8,
    elevation: 4,
  },
  venueImage: {
    height: 160,
  },
  placeholderImage: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  placeholderText: {
    textAlign: 'center',
    marginBottom: 4,
  },
  venueContent: {
    padding: 12,
  },
  venueName: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  venueDescription: {
    marginTop: 4,
    marginBottom: 8,
  },
  venueRating: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  ratingText: {
    fontWeight: 'bold',
  },
  blogContainer: {
    marginBottom: 24,
  },
  blogCard: {
    elevation: 4,
  },
  blogTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
  },
  blogDate: {
    marginBottom: 12,
  },
  blogExcerpt: {
    marginBottom: 16,
    lineHeight: 20,
  },
  blogButton: {
    alignSelf: 'flex-start',
  },
  // Error state styles
  errorContainer: {
    marginBottom: 24,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
  },
  errorContent: {
    alignItems: 'center',
    padding: 24,
  },
  errorIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  errorTitle: {
    marginBottom: 12,
    textAlign: 'center',
  },
  errorMessage: {
    marginBottom: 8,
    textAlign: 'center',
  },
  errorHint: {
    marginBottom: 24,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  retryButton: {
    marginTop: 16,
  },
  // Loading state styles
  loadingContainer: {
    marginBottom: 24,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
  },
  loadingContent: {
    alignItems: 'center',
    padding: 24,
  },
  loadingIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  loadingTitle: {
    marginBottom: 12,
    textAlign: 'center',
  },
  loadingMessage: {
    textAlign: 'center',
  },
  debugButton: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#007AFF',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  debugButtonText: {
    fontSize: 20,
    color: 'white',
  },
}); 